import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Progressively enhances the grid's next/previous links with automatic loading.
 * Requests, observers and rendered pages belong to one filter/sort generation.
 * @extends Component
 */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #listeners;
  #requests = new Map();
  #busy = new Set();
  #failed = new Set();
  #generation = 0;
  #query = '';
  #filterPending = false;
  #filterEpoch = 0;
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate, options);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate, options);
    scrollContainerMediaQuery.addEventListener('change', this.#observeViewMore, options);
    window.addEventListener('popstate', this.#handlePopState, options);
    this.addEventListener('click', this.#handleLoadClick, options);
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    // Section morphs can preserve this element, including when the page count is unchanged.
    this.#reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#filterEpoch++;
    this.#filterPending = false;
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#cancelRequests();
  }

  #queryKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return `${url.pathname}?${url.searchParams}`;
  }

  #cancelRequests() {
    this.#generation++;
    for (const request of this.#requests.values()) request.controller.abort();
    this.#requests.clear();
    this.#busy.clear();
    this.pages.clear();
  }

  #reset() {
    this.#cancelRequests();
    this.#query = this.#queryKey();
    this.#failed.clear();
    this.#updateControls();
    this.#observeViewMore();
  }

  #observeViewMore = () => {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    if (!this.isConnected || this.#filterPending || this.getAttribute('infinite-scroll') === 'false') return;
    if (!('IntersectionObserver' in window)) return; // Native links remain usable.
    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        const generation = this.#generation;
        if (viewTransition.current) await viewTransition.current;
        if (!this.isConnected || generation !== this.#generation) return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const type = entry.target.dataset.pageDirection;
          if ((type === 'next' || type === 'previous') && !this.#failed.has(type)) {
            void this.#renderPage(type);
          }
        }
      },
      { root: getIntersectionRoot(), rootMargin: '100px 0px' }
    );
    for (const type of ['previous', 'next']) {
      const control = this.#control(type);
      if (control && this.#getPage(type)) this.infinityScrollObserver.observe(control);
    }
  };

  #control(type) {
    return this.querySelector(`[data-page-direction="${type}"]`);
  }

  #cards() {
    return Array.from(this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]') ?? []);
  }

  #getPage(type) {
    const cards = this.#cards();
    const card = cards[type === 'previous' ? 0 : cards.length - 1];
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    if (!card || !Number.isInteger(lastPage)) return;
    const page = Number(card.dataset.page) + (type === 'previous' ? -1 : 1);
    if (page < 1 || page > lastPage) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  #updateControls() {
    for (const type of ['previous', 'next']) {
      const control = this.#control(type);
      if (!control) continue;
      const page = this.#getPage(type);
      const link = control.querySelector('a');
      const status = control.querySelector('[role="status"]');
      const busy = this.#busy.has(type);
      const failed = this.#failed.has(type);
      control.hidden = type === 'previous' && !page;
      if (link) {
        link.hidden = !page;
        if (page) link.href = page.url.toString();
        link.setAttribute('aria-disabled', String(busy || this.#filterPending));
        link.textContent = failed ? control.dataset.retry : control.dataset.label;
      }
      control.setAttribute('aria-busy', String(busy));
      if (status) status.textContent = busy ? control.dataset.loading : failed ? control.dataset.error :
        (!page && type === 'next' && !this.#getPage('previous') ? control.dataset.end : '');
    }
    this.refs.grid?.setAttribute('aria-busy', String(this.#busy.size > 0));
  }

  #handleLoadClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-page-direction] a');
    if (!link || !this.contains(link) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const type = link.closest('[data-page-direction]').dataset.pageDirection;
    if (type !== 'next' && type !== 'previous') return;
    event.preventDefault();
    void this.#renderPage(type);
  };

  async #fetchPage(page, generation) {
    if (this.pages.has(page.page)) return this.pages.get(page.page);
    const existing = this.#requests.get(page.page);
    if (existing) return existing.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    const request = { controller, promise: undefined };
    request.promise = (async () => {
      // Do not reuse a cached error document; each explicit retry may reach the server.
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, new URL(page.url), controller.signal);
      if (controller.signal.aborted || generation !== this.#generation) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const grid = parsed.querySelector('[ref="grid"]');
      const cards = Array.from(grid?.querySelectorAll(':scope > [ref="cards[]"]') ?? []);
      if (!cards.length || cards.some((card) => Number(card.dataset.page) !== page.page)) {
        throw new Error('Invalid product grid response');
      }
      this.pages.set(page.page, html);
      return html;
    })().finally(() => {
      clearTimeout(timeout);
      if (this.#requests.get(page.page) === request) this.#requests.delete(page.page);
    });
    this.#requests.set(page.page, request);
    return request.promise;
  }

  async #renderPage(type) {
    const grid = this.refs.grid;
    const page = this.#getPage(type);
    if (!grid || !page || !this.isConnected || this.#filterPending || this.#busy.has(type)) return;
    if (this.#queryKey() !== this.#query) return;
    const generation = this.#generation;
    this.#busy.add(type);
    this.#failed.delete(type);
    this.#updateControls();
    try {
      const html = await this.#fetchPage(page, generation);
      if (!html || !this.isConnected || generation !== this.#generation || this.refs.grid !== grid ||
          this.#queryKey() !== this.#query || this.#getPage(type)?.page !== page.page) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = Array.from(parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]'));
      const ids = new Set(this.#cards().map((card) => card.dataset.productId || card.id));
      const cards = incoming.filter((card) => {
        const id = card.dataset.productId || card.id;
        if (!id || ids.has(id)) return false;
        ids.add(id);
        return true;
      });
      if (!cards.length) throw new Error('Product grid page contains no new products');
      const first = grid.querySelector('[ref="cards[]"]');
      const oldTop = first?.getBoundingClientRect().top;
      const scrollTop = getScrollTop();
      if (type === 'previous') grid.prepend(...cards);
      else grid.append(...cards);
      super.updatedCallback();
      this.#aspectRatioHelper?.processNewElements();
      if (type === 'previous' && first && oldTop !== undefined) {
        scrollTo({ top: scrollTop + first.getBoundingClientRect().top - oldTop, behavior: 'instant' });
      }
      // Automatic loading is not a user navigation: retain one history entry for this query.
      history.replaceState(history.state, '', page.url.toString());
      this.pages.delete(page.page);
    } catch (error) {
      if (generation === this.#generation) this.#failed.add(type);
    } finally {
      if (generation === this.#generation) {
        this.#busy.delete(type);
        this.#updateControls();
        // Reobserve after layout changes, including a sentinel which stays inside the root.
        this.#observeViewMore();
      }
    }
  }

  #handleFilterUpdate = (event) => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    if (!section || section.id !== `shopify-section-${this.sectionId}`) return;
    this.#filterPending = true;
    this.#cancelRequests();
    const epoch = ++this.#filterEpoch;
    this.infinityScrollObserver?.disconnect();
    this.#updateControls();
    // The producer supplies a completion promise; morph also invokes updatedCallback.
    let failed = false;
    Promise.resolve(event.promise).catch(() => { failed = true; }).finally(() => {
      if (!this.isConnected || epoch !== this.#filterEpoch) return;
      // Never mix a new query's following page with an old/unmorphed first page.
      if (failed || this.#query !== this.#queryKey()) {
        window.location.reload();
        return;
      }
      this.#filterPending = false;
      super.updatedCallback();
      this.#reset();
    });
  };

  #handlePopState = () => {
    // Same-query Back keeps the rendered cards/scroll. A different filter query needs a fresh grid.
    if (this.#queryKey() !== this.#query) {
      this.#cancelRequests();
      window.location.reload();
    }
  };

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}
